// Pure, side-effect-free JWT helpers. Deliberately free of Vue, Pinia and DOM dependencies so
// the sync worker can import them.

/**
 * Claims that identify *who* a token belongs to, in the order we trust them. Anything volatile
 * (exp, iat, jti, session identifiers) is deliberately excluded: this value has to stay stable
 * across separate sign-ins by the same person, otherwise it would invalidate their cache on
 * every login — the exact behavior it exists to prevent.
 */
const IDENTITY_CLAIMS: readonly string[] = [
    'sub',
    'nameid',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
    'unique_name',
    'userId',
    'user_id',
    'uid',
    'email'
];

function decodeJwtPayload( jwt: string ): Record< string, unknown > | null {
    try {
        // JWT structure: header.payload.signature
        const parts = jwt.split( '.' );
        if ( 3 !== parts.length ) {
            return null;
        }

        const decoded = atob( parts[ 1 ].replace( /-/g, '+' ).replace( /_/g, '/' ) );
        const payload = JSON.parse( decoded );

        return ( payload && 'object' === typeof payload ) ? payload as Record< string, unknown > : null;
    } catch {
        return null;
    }
}

/**
 * FNV-1a. Not a security primitive — this only has to answer "is this the same person as last
 * time?" without writing a raw email or account id into yet another storage location.
 */
function fingerprint( value: string ): string {
    let hash = 0x811c9dc5;
    for ( let i = 0; i < value.length; i++ ) {
        hash ^= value.charCodeAt( i );
        hash = Math.imul( hash, 0x01000193 );
    }
    return ( hash >>> 0 ).toString( 36 );
}

/**
 * A stable, opaque identifier for the account a token belongs to, used to detect that the local
 * cache has changed hands.
 *
 * Returns `null` when no identity claim can be found, which callers must treat as "cannot tell" —
 * never as "different user". Guessing wrong in that direction would throw away a cache for no
 * reason, so the safe default is to leave it alone.
 */
export function deriveCacheOwnerId( jwt: string | null | undefined ): string | null {
    if ( !jwt ) {
        return null;
    }

    const payload = decodeJwtPayload( jwt );
    if ( !payload ) {
        return null;
    }

    for ( const claim of IDENTITY_CLAIMS ) {
        const value = payload[ claim ];
        if ( 'string' === typeof value && value.length > 0 ) {
            return fingerprint( `${ claim }:${ value }` );
        }
        if ( 'number' === typeof value ) {
            return fingerprint( `${ claim }:${ value }` );
        }
    }

    return null;
}
